import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    role: '',
    industry: 'Retail',
    stores: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/demo-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit form');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="min-h-[70vh] flex items-center justify-center bg-white px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
            <CheckCircle2 size={40} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold font-heading text-foreground mb-4">Request Received.</h2>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            Thank you for your interest in ProAct AI. Our solutions expert will reach out to you within <span className="font-bold text-foreground">1 business day</span> to schedule your personal walkthrough.
          </p>
          <button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                fullName: '',
                email: '',
                company: '',
                role: '',
                industry: 'Retail',
                stores: '',
                message: ''
              });
            }}
            data-testid="submit-another"
            className="text-primary font-semibold hover:underline"
          >
            Submit another request
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="bg-white">
      {/* Hero */}
      <section className="py-16 bg-secondary/30">
        <div className="container-custom text-center">
          <AnimatedSection>
            <h1 className="text-4xl lg:text-6xl font-extrabold font-heading text-foreground mb-6">
              Ready to See the <span className="text-gradient">Invisible?</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Fill in the details below to request a personalized demo or pilot program for your stores.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <form 
              onSubmit={handleSubmit} 
              data-testid="contact-form"
              className="space-y-6 bg-white p-8 md:p-12 rounded-2xl border border-border shadow-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Full Name *</label>
                  <input 
                    required 
                    type="text" 
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    data-testid="input-fullname"
                    placeholder="John Doe" 
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Work Email *</label>
                  <input 
                    required 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="input-email"
                    placeholder="john@company.com" 
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Company Name *</label>
                  <input 
                    required 
                    type="text" 
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="input-company"
                    placeholder="Acme Retail" 
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Role *</label>
                  <input 
                    required 
                    type="text" 
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    data-testid="input-role"
                    placeholder="Head of Operations" 
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Industry</label>
                  <select 
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    data-testid="input-industry"
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option>Retail</option>
                    <option>Quick Service Restaurant (QSR)</option>
                    <option>Malls / Physical Spaces</option>
                    <option>Partner Inquiry</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">Number of Stores</label>
                  <input 
                    type="text" 
                    name="stores"
                    value={formData.stores}
                    onChange={handleChange}
                    data-testid="input-stores"
                    placeholder="e.g. 50-100" 
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">Primary Goal / Challenge</label>
                <textarea 
                  rows={4} 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="input-message"
                  placeholder="Tell us what you're trying to solve..." 
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                ></textarea>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                disabled={isLoading}
                data-testid="submit-button"
                className="w-full bg-primary text-white py-4 rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-primary/90 transition-all shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" /> Request Demo / Pilot
                  </>
                )}
              </button>
              <p className="text-center text-sm text-muted-foreground">
                By clicking request, you agree to our privacy policy and terms.
              </p>
            </form>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Contact;
